import { useEffect, useMemo, useState } from 'react';

import { Accordion, ChakraBox } from '@equip.health/ui';

import { ApiCommand } from '~/lib/Api';
import { PatientNoteListItemType } from '~/lib/constants/AddPatientNoteModal.constants';
import urlConstants from '~/lib/constants/url.constants';
import { useApi } from '~/lib/hooks';
import { isPastDateTime } from '~/lib/util/classes/groupClasses.util';
import Loading from '~/pages/loading';
import {
  useGetGroupClassNotesByEventExternalIdsQuery,
  useGetGroupSectionNotesByEventExternalIdsQuery,
} from '~/store/services/notes/notes';
import { useFlags } from 'launchdarkly-react-client-sdk';

import PatientNoteItem from '../profile/tabs/patientNotesTab/PatientNoteItem';
import {
  useGetGroupSectionEventDetailsQuery,
  useGetRegistrantsQuery,
} from '~/store/services/groupClass';
import {
  mapGroupEventHostsToAttendees,
  splitRegistrantsByDeclineStatus,
} from '~/lib/util/groups.utils';

interface ViewNotesAccordionProps {
  dismissViewNote?: () => void;
  groupClassEventExternalId: string;
  isExpanded?: boolean;
  setIsExpanded?: (expanded: boolean) => void;
}

const TOP_LEVEL_ID = 'view-notes-accordion';

const ViewGroupClassNoteAccordion = ({
  dismissViewNote,
  groupClassEventExternalId,
  isExpanded,
  setIsExpanded,
}: ViewNotesAccordionProps) => {
  const { groupsRestructuring: isGroupsRestructuringEnabled } = useFlags();

  const {
    loading: isGetGroupClassEventConfirmedAttendeesLoading,
    sendRequest: getGroupClassEventConfirmedAttendeesRequest,
  } = useApi<BaseAPIResponse<GroupClassEventAttendee[]>>();

  const {
    data: groupClassEventDetails,
    loading: isGetGroupClassEventDetailsLoading,
    sendRequest: getGroupClassEventDetails,
  } = useApi<BaseAPIResponse<GroupClassEventDetails>>();
  const [attendees, setAttendees] = useState<GroupClassEventAttendee[]>([]);

  const {
    data: groupSectionEventRegistrantsData,
    isFetching: isGetRegistrantsFetching,
  } = useGetRegistrantsQuery(groupClassEventExternalId, {
    skip: !groupClassEventExternalId || !isGroupsRestructuringEnabled,
  });

  const {
    data: groupSectionEventDetails,
    isFetching: isGetGroupSectionEventDetailsFetching,
  } = useGetGroupSectionEventDetailsQuery(groupClassEventExternalId, {
    skip: !groupClassEventExternalId || !isGroupsRestructuringEnabled,
  });

  const { declinedUsers, registeredUsers } = useMemo(
    () => splitRegistrantsByDeclineStatus(groupSectionEventRegistrantsData),
    [groupSectionEventRegistrantsData],
  );

  const mappedGroupSectionEventDetails = useMemo<GroupClassEventDetails>(() => {
    if (!groupSectionEventDetails) return null;

    return {
      attendees: registeredUsers,
      declinedUsers,
      endTime: groupSectionEventDetails.endTime,
      frequency: groupSectionEventDetails.frequency as RecurrenceFrequencyValue,
      groupClassEventExternalId,
      groupClassExternalId: groupSectionEventDetails.groupId,
      hosts: mapGroupEventHostsToAttendees(groupSectionEventDetails.hosts),
      iconName: '',
      startTime: groupSectionEventDetails.startTime,
      title: groupSectionEventDetails.title,
    };
  }, [
    declinedUsers,
    groupClassEventExternalId,
    groupSectionEventDetails,
    registeredUsers,
  ]);

  const { data: groupClassNotesData } =
    useGetGroupClassNotesByEventExternalIdsQuery([groupClassEventExternalId], {
      skip: isGroupsRestructuringEnabled,
    });

  const { data: groupSectionNotesData } =
    useGetGroupSectionNotesByEventExternalIdsQuery(
      [groupClassEventExternalId],
      {
        skip: !isGroupsRestructuringEnabled,
      },
    );

  const note = (
    isGroupsRestructuringEnabled ? groupSectionNotesData : groupClassNotesData
  )?.[0]; // more than one note for an event is not anticipated

  useEffect(() => {
    if (!isGroupsRestructuringEnabled && groupClassEventExternalId) {
      getGroupClassEventDetails({
        command: ApiCommand.GET,
        url: urlConstants.groupClasses.getGroupClassEventDetails(
          groupClassEventExternalId,
        ),
      });
    }
  }, [groupClassEventExternalId, isGroupsRestructuringEnabled]);

  useEffect(() => {
    if (!isGroupsRestructuringEnabled && groupClassEventDetails) {
      setAttendees(
        groupClassEventDetails?.data?.attendees?.map((attendee) => ({
          ...attendee,
          attended: true,
        })) ?? [],
      );
      if (isPastDateTime(groupClassEventDetails?.data.endTime)) {
        getGroupClassEventConfirmedAttendeesRequest({
          callback: (confirmedAttendees) =>
            setAttendees(confirmedAttendees?.data),
          command: ApiCommand.GET,
          url: urlConstants.groupClasses.getGroupClassEventConfirmedAttendees(
            groupClassEventExternalId,
          ),
        });
      }
    }
  }, [
    groupClassEventDetails,
    groupClassEventExternalId,
    isGroupsRestructuringEnabled,
  ]);

  if (
    isGetGroupClassEventConfirmedAttendeesLoading ||
    isGetGroupClassEventDetailsLoading ||
    isGetRegistrantsFetching ||
    isGetGroupSectionEventDetailsFetching
  ) {
    return <Loading />;
  }
  return (
    <Accordion
      dismissMenu={dismissViewNote}
      id={TOP_LEVEL_ID}
      isExpanded={isExpanded}
      panelContentMaxHeight="68vh"
      panelMaxWidth="60vw"
      setIsExpanded={setIsExpanded}
      title="View note"
    >
      {note && (
        <ChakraBox
          id={`${TOP_LEVEL_ID}__${note.noteExternalId}`}
          key={`Notes::${note.noteExternalId}}`}
          minWidth="60vw"
          padding="16px"
        >
          <PatientNoteItem
            confirmedAttendees={
              isGroupsRestructuringEnabled ? registeredUsers : attendees
            }
            groupClassEventDetails={
              isGroupsRestructuringEnabled
                ? mappedGroupSectionEventDetails
                : groupClassEventDetails?.data
            }
            note={note as unknown as PatientNoteListItemType} // TODO remove cast
          />
        </ChakraBox>
      )}
    </Accordion>
  );
};

export default ViewGroupClassNoteAccordion;
